import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzSingleSelectWebComponent } from '../../../../../../../../ui/gzSingleSelectDropdown';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import {
  PolicyDependenciesContainer,
  ProtectedEntitiesEPSApiClients,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { LabelWidthValues } from '../../../../../../../../types/common/types';
import { GzHardCodedFormField } from '../../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import {
  CommunicationServerMachineAndSUPA,
  CommunicationServerMachineAndSUPARequest,
  EcsAssignmentFormValue,
} from '@nirvana/shared/dtos/types/policies/common';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_COMMUNICATION_ENDPOINTS_ASSIGNMENT_ITEM } from '../../../../../../../../webcomponents.constants';
import { customElement, query } from 'lit/decorators.js';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../ui/gzInput';
import { RequiredValidator } from 'apps/webcomponents/src/ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import { MAX_CUSTOM_HOSTNAME_LENGTH } from './GzEcsAssignmentGridWebComponent';
import { OnNewGridItemAdd } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/events/events';
import { FormGroupValue } from '../../../../../../../../ui/gzForm/formGroup/types/form-group-value';

@customElement(WEB_COMPONENT_POLICY_PANEL_COMMUNICATION_ENDPOINTS_ASSIGNMENT_ITEM)
export class GzEcsAssignmentItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#name-form-field')
  private nameFormField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('#ip-form-field')
  private ipFormField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('#custom-name-form-field')
  private itemCustomHostname: GzFormFieldWebComponent<GzInputWebComponent, string>;

  private itemName: GzHardCodedFormField<string> = new GzHardCodedFormField('');
  private itemIp: GzHardCodedFormField<string> = new GzHardCodedFormField('');
  @query('#itemPriority')
  private itemPriority: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  private itemType: GzHardCodedFormField<number | undefined> = new GzHardCodedFormField(0);
  private itemUrl: GzHardCodedFormField<string> = new GzHardCodedFormField('');
  private itemId: GzHardCodedFormField<string> = new GzHardCodedFormField('');

  private clients: ProtectedEntitiesEPSApiClients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'ProtectedEntitiesEPS'
  ) as ProtectedEntitiesEPSApiClients;
  private currentEcsMachines: CommunicationServerMachineAndSUPA[] = [];

  protected setValidators() {
    this.ipFormField.Validators = [RequiredValidator('Required field')];
    this.nameFormField.Validators = [RequiredValidator('Required field')];
    this.itemCustomHostname.Validators = [
      MaxLengthValidator(
        MAX_CUSTOM_HOSTNAME_LENGTH,
        translateFn('The custom hostname must not exceed {maxLength} characters.', {
          maxLength: MAX_CUSTOM_HOSTNAME_LENGTH,
        })
      ),
    ];
  }

  protected createFormGroup(): void {
    this.formGroup = new FormGroup({
      defaultPositionInGrid: this.itemPriority,
      customHostname: this.itemCustomHostname,
      ip: this.itemIp,
      name: this.itemName,
      protectedEntityId: this.itemId,
      type: this.itemType,
      url: this.itemUrl,
      ipFormField: this.ipFormField,
      nameFormField: this.nameFormField,
    });
    this.setValidators();
  }

  protected async onNameSearchChanged(event: CustomEvent) {
    await this.getItems('name', event.detail.value);
  }

  protected async onIpSearchChanged(event: CustomEvent) {
    await this.getItems('specifics.ip', event.detail.value);
  }

  protected renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemPriority">
          <gz-input-number
            gz-form-input
            .min=${1}
            inputWidth="${InputWidthValues.SMALL}"
            label="${translateFn('Priority')}"
          ></gz-input-number>
        </gz-form-field>
      </div>
      <div class="policy-grid-item-form--field">
        <gz-form-field id="name-form-field">
          <gz-single-select
            id="name-single-select"
            gz-form-input
            labelWidth="${LabelWidthValues.LARGE}"
            hasDynamicSearch="${true}"
            label="${translateFn('Name')}"
            placeholder="${translateFn('No relay selected')}"
            searchPlaceholderString="${translateFn('Search')}"
            noSearchResultsString="${translateFn('No relay found.')}"
            @open="${this.onNameDropdownOpen}"
            @selectionChange="${this.onSelectionChanged}"
            @searchInputChange="${this.onNameSearchChanged}"
          </gz-single-select>
          </gz-form-field>
      </div>
      <div class="policy-grid-item-form--field">
        <gz-form-field id="ip-form-field">
          <gz-single-select
            id="ip-single-select"
            gz-form-input
            labelWidth="${LabelWidthValues.LARGE}"
            hasDynamicSearch="${true}"
            label="${translateFn('IP address')}"
            placeholder="${translateFn('No IP address selected')}"
            searchPlaceholderString="${translateFn('Search')}"
            noSearchResultsString="${translateFn('No IP address found.')}"
            @open="${this.onIpDropdownOpen}"
            @selectionChange="${this.onSelectionChanged}"
            @searchInputChange="${this.onIpSearchChanged}"
          ></gz-single-select
        ></gz-form-field>
      </div>
      <div class="policy-grid-item-form--field">
        <gz-form-field id="custom-name-form-field"
          ><gz-input gz-form-input label="${translateFn('Custom name/IP')}" inputWidth="${
      InputWidthValues.LARGE
    }"></gz-input
        ></gz-form-field>
      </div>`;
  }

  private async onNameDropdownOpen() {
    await this.getItems('name', '');
  }

  private async onIpDropdownOpen() {
    await this.getItems('specifics.ip', '');
  }

  private async getItems(key: string, value: string) {
    const filterProperty = key === 'name' ? key : 'specifics.ip';
    this.ipFormField.Input.isLoading = true;
    this.nameFormField.Input.isLoading = true;

    try {
      const response = await this.clients.getCommunicationServerMachinesAndSUPA(
        this.constructRequest(filterProperty, value)
      );
      this.currentEcsMachines = response.data;
      this.ipFormField.Input.filterOptions = this.currentEcsMachines.map(item => {
        return {
          label: item.ip,
          value: item._id,
        };
      });
      this.nameFormField.Input.filterOptions = this.currentEcsMachines.map(item => {
        return {
          label: item.name,
          value: item._id,
        };
      });
    } catch (error) {
      this.setError(translateFn('Unable to load the list of Relays or Communication Servers.'));
      this.currentEcsMachines = [];
    } finally {
      this.ipFormField.Input.isLoading = false;
      this.nameFormField.Input.isLoading = false;
    }
  }

  protected addNewItem() {
    this.dispatchEvent(new OnNewGridItemAdd(this.mapToEcsAssignmentFormValue(this.formGroup.value)));
  }

  private mapToEcsAssignmentFormValue(value: FormGroupValue): EcsAssignmentFormValue {
    return {
      customHostname: value.customHostname as string,
      defaultPositionInGrid: value.defaultPositionInGrid as number,
      ip: value.ip as string,
      name: value.name as string,
      protectedEntityId: value.protectedEntityId as string,
      type: value.type as number,
      url: value.url as string,
    };
  }

  private onSelectionChanged(event: CustomEvent): void {
    let data: CommunicationServerMachineAndSUPA | undefined;

    if (event.detail.selectedValue) {
      data = this.currentEcsMachines.find(item => item._id === event.detail.selectedValue);
    }

    if (!data) {
      return;
    }
    if (this.nameFormField.value !== event.detail.selectedValue) {
      this.nameFormField.value = event.detail.selectedValue;
      this.nameFormField.validate(true);
    }

    if (this.ipFormField.value !== event.detail.selectedValue) {
      this.ipFormField.value = event.detail.selectedValue;
      this.ipFormField.validate(true);
    }
    const ipValue = data.ip ?? '';
    this.itemName.value = data.name ?? '';

    if (ipValue === '-' || ipValue === 'N/A') {
      this.itemCustomHostname.value = data.customHostname ?? '';
    } else {
      this.itemCustomHostname.value = '';
    }

    this.itemIp.value = ipValue;
    this.itemUrl.value = data.url ?? '';
    this.itemId.value = data._id ?? '';
    this.itemType.value = data.type;
  }

  focusFirstInput(): void {
    this.itemPriority.Input.inputFocus();
  }

  reset(): void {
    this.resetForm();
  }

  resetForm(): void {
    this.nameFormField.value = '';
    this.ipFormField.value = '';
    this.nameFormField.resetField();
    this.ipFormField.resetField();

    this.itemName.value = '';
    this.itemIp.value = '';
    this.itemCustomHostname.value = '';
    this.itemCustomHostname.resetField();
    this.itemUrl.value = '';
    this.itemId.value = '';
    this.itemType.value = undefined;

    this.nameFormField.validate();
    this.ipFormField.validate();
    this.itemCustomHostname.validate();
  }

  resetOnAdd(): void {
    this.resetForm();
  }

  public resetPrioritySettings(max: number) {
    this.itemPriority.Input.max = max;
    this.itemPriority.value = max;
  }

  public setError(error: string) {
    this.itemCustomHostname.setError(error);
  }

  constructRequest(key: string, value: string): CommunicationServerMachineAndSUPARequest {
    return {
      page: 1,
      limit: 100,
      filters: [
        {
          property: key,
          value: value,
        },
      ],
    };
  }
}
